import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MicroLearningModule from '../components/MicroLearning/MicroLearningModule';
import FeedbackPrompt from '../components/feedback/FeedbackPrompt';
import PastESSLCEQuiz from '../components/MicroLearning/PastESSLCEQuiz';
// CoachCard hidden for launch — re-enable once coach logic is rebuilt
// import CoachCard, { CoachAction } from '../components/CoachCard/CoachCard';
import { displayTopic } from '../config/topicDisplayNames';
import Logo from '../components/Logo';
import './MicroLearning.css';

interface ModuleProgress {
  status: 'not_started' | 'in_progress' | 'completed';
  completion_percentage: number;
  score: number;
  max_score: number;
  correct_answers?: number;
  total_questions_answered?: number;
  started_at?: string;
  completed_at?: string;
}

interface Module {
  module_id: string;
  title: string;
  description?: string;
  subject: string;
  topic: string;
  subtopic?: string;
  grade_level?: number;
  total_components?: number;
  estimated_duration_minutes?: number;
  difficulty_level?: string;
  status?: string;
  is_published?: boolean;
  student_progress?: ModuleProgress;
  diagnostic_label?: 'focus_area' | 'start_learning' | 'solid';
}


const MicroLearning: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Read navigation state from Dashboard coach card actions
  const navState = location.state as any;

  // State
  const [selectedSubject, setSelectedSubject] = useState<string>(
    navState?.defaultSubject || ''
  );
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTabRaw] = useState<'module' | 'practice'>(() => {
    // Priority: explicit nav state > localStorage memory > default (past exams).
    // Past Exams is the landing tab — it's what students actually came for.
    if (navState?.defaultTab === 'past_exams') return 'practice';
    if (navState?.defaultTab === 'module') return 'module';
    try {
      const last = localStorage.getItem('lomi_last_tab');
      if (last === 'lessons') return 'module';
    } catch {}
    return 'practice';
  });
  // Persist tab choice so Dashboard can restore it
  const setActiveTab = (tab: 'module' | 'practice') => {
    setActiveTabRaw(tab);
    try { localStorage.setItem('lomi_last_tab', tab === 'practice' ? 'past_exams' : 'lessons'); } catch {}
  };
  const [pendingAutoStartModule, setPendingAutoStartModule] = useState<string | null>(
    navState?.autoStartModule || null
  );
  const [pendingDefaultTopic, setPendingDefaultTopic] = useState<string | null>(
    navState?.defaultTopic || null
  );

  // Data
  const [modules, setModules] = useState<Module[]>([]);
  const [loadingModules, setLoadingModules] = useState(false);
  // ESLCE topic frequency (topic name → percentage of exam)
  const [eslceTopicPcts, setEslceTopicPcts] = useState<Record<string, number>>({});

  // Selected module for learning
  const [activeModule, setActiveModule] = useState<Module | null>(null);

  // Section expansion state (for 20+ modules)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const SHOW_COUNT_COMPLETED = 4;
  const SHOW_COUNT_UP_NEXT = 8;

  const getSubjectsByStream = () => {
    if (student?.stream === 'natural') {
      return ['Biology', 'Chemistry', 'Physics', 'Mathematics', 'English', 'Aptitude'];
    } else if (student?.stream === 'social') {
      return ['History', 'Geography', 'Economics', 'Mathematics', 'English', 'Aptitude'];
    }
    return ['Biology', 'Chemistry', 'Physics', 'Mathematics', 'English', 'Aptitude', 'Geography', 'History', 'Economics'];
  };

  const availableSubjects = getSubjectsByStream();

  // Subject colors for visual identity
  const subjectColors: Record<string, string> = {
    'Biology': '#4CAF50',
    'Chemistry': '#2196F3',
    'Physics': '#FF9800',
    'Mathematics': '#9C27B0',
    'History': '#795548',
    'Geography': '#009688',
    'Economics': '#FF5722',
    'Civics': '#607D8B',
    'English': '#3F51B5',
    'Aptitude': '#E91E63',
  };

  // Load modules + ESLCE topic counts when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadModules();
      loadEslceTopicPcts();
    } else {
      setModules([]);
      setEslceTopicPcts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  const loadModules = async () => {
    try {
      setLoadingModules(true);
      // Use the admin API to get published modules
      const params = new URLSearchParams({
        subject: selectedSubject,
        status: 'published'
      });
      if (student?.grade_level) {
        params.append('grade_level', student.grade_level.toString());
      }
      // Pass student_id to get progress data
      if (student?.student_id) {
        params.append('student_id', student.student_id);
      }

      const response = await authFetch(`/api/micro-learning/modules?${params}`);
      const data = await response.json();

      if (data.success) {
        setModules(data.modules || []);
      } else {
        setModules([]);
      }
    } catch (error) {
      console.error('Failed to load modules:', error);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const loadEslceTopicPcts = async () => {
    try {
      const response = await authFetch(`/api/questions/filter-options?subject=${encodeURIComponent(selectedSubject)}`);
      const data = await response.json();
      if (data.success && data.topics) {
        const pcts: Record<string, number> = {};
        data.topics.forEach((t: { name: string; pct: number }) => { pcts[t.name] = t.pct; });
        setEslceTopicPcts(pcts);
      }
    } catch {
      // Non-critical — topic percentages are supplementary
    }
  };

  const handleSubjectSelect = (subject: string) => {
    setSelectedSubject(subject);
    setSelectedTopic('');
    setActiveModule(null);
    // Persist last subject so Dashboard coach card can use it
    try { localStorage.setItem('lomi_last_subject', subject); } catch {}
  };

  const handleTopicSelect = (topic: string) => {
    setSelectedTopic(topic);
  };

  // Track if we're entering review mode for completed modules
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [justCompletedModule, setJustCompletedModule] = useState(false);

  const handleModuleSelect = (module: Module) => {
    // If module is completed, enter review mode
    const isCompleted = module.student_progress?.status === 'completed';
    setIsReviewMode(isCompleted);
    setActiveModule(module);
  };

  const handleBackToModules = () => {
    setActiveModule(null);
    setIsReviewMode(false);
  };

  const handleModuleComplete = (summary: any) => {
    console.log('Module completed!', summary);
    // Preserve the subject from the completed module before clearing
    const completedSubject = activeModule?.subject;

    // Ensure subject is set before clearing activeModule
    if (completedSubject) {
      setSelectedSubject(completedSubject);
    }

    setActiveModule(null);
    setIsReviewMode(false);
    setJustCompletedModule(true); // earned-moment → Signal Engine feedback prompt
  };

  // Reload modules when returning from a session (activeModule becomes null)
  useEffect(() => {
    if (!activeModule && selectedSubject) {
      loadModules();
    }
  }, [activeModule, selectedSubject]);

  // Auto-start module from Dashboard navigation (e.g. "Continue" button)
  useEffect(() => {
    if (pendingAutoStartModule && modules.length > 0) {
      const mod = modules.find(m => m.module_id === pendingAutoStartModule);
      if (mod) {
        handleModuleSelect(mod);
      }
      setPendingAutoStartModule(null);
    }
  }, [pendingAutoStartModule, modules]);

  // Clear pending topic after it's been consumed by PastESSLCEQuiz
  useEffect(() => {
    if (pendingDefaultTopic && activeTab === 'practice') {
      // Give PastESSLCEQuiz time to read the prop, then clear it
      const timer = setTimeout(() => setPendingDefaultTopic(null), 500);
      return () => clearTimeout(timer);
    }
  }, [pendingDefaultTopic, activeTab]);

  // Count modules per topic (for "N of M" indicator on multi-module topics)
  const topicModuleCounts: Record<string, number> = {};
  modules.forEach(m => {
    topicModuleCounts[m.topic] = (topicModuleCounts[m.topic] || 0) + 1;
  });

  // Pre-compute sorted index within each topic (for stable "N of M" numbering)
  const moduleTopicIndex: Record<string, number> = {};
  const topicGrouped: Record<string, Module[]> = {};
  modules.forEach(m => {
    if (!topicGrouped[m.topic]) topicGrouped[m.topic] = [];
    topicGrouped[m.topic].push(m);
  });
  Object.values(topicGrouped).forEach(group => {
    group.sort((a, b) => a.title.localeCompare(b.title));
    group.forEach((m, i) => { moduleTopicIndex[m.module_id] = i + 1; });
  });

  // Filter modules based on topic and search
  const filteredModules = modules.filter(module => {
    const matchesTopic = !selectedTopic || module.topic === selectedTopic;
    const matchesSearch = !searchQuery ||
      module.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (module.subtopic || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTopic && matchesSearch;
  });

  // Smart sequencing: In Progress → Not Started → Completed
  const getStatusPriority = (status: string | undefined) => {
    if (status === 'in_progress') return 0;
    if (!status || status === 'not_started') return 1;
    if (status === 'completed') return 2;
    return 1;
  };

  // Diagnostic priority: focus_area first, then start_learning, then solid/none
  const getDiagnosticPriority = (label: string | undefined) => {
    if (label === 'focus_area') return 0;
    if (label === 'start_learning') return 1;
    if (!label) return 2;  // No diagnostic data — neutral
    return 3;  // solid — lowest priority
  };

  const sortedModules = [...filteredModules].sort((a, b) => {
    const statusA = a.student_progress?.status;
    const statusB = b.student_progress?.status;
    const priorityDiff = getStatusPriority(statusA) - getStatusPriority(statusB);
    if (priorityDiff !== 0) return priorityDiff;
    // Within same status group, sort by diagnostic label
    const diagDiff = getDiagnosticPriority(a.diagnostic_label) - getDiagnosticPriority(b.diagnostic_label);
    if (diagDiff !== 0) return diagDiff;
    // Then by topic, then title
    if (a.topic !== b.topic) return a.topic.localeCompare(b.topic);
    return a.title.localeCompare(b.title);
  });

  // Group modules by status for section headers
  // Threshold: need >=10% completion to appear in "Continue" section (avoids barely-tapped modules)
  const CONTINUE_THRESHOLD = 10;
  const inProgressModules = sortedModules.filter(m =>
    m.student_progress?.status === 'in_progress' &&
    (m.student_progress?.completion_percentage || 0) >= CONTINUE_THRESHOLD
  );
  const notStartedModules = sortedModules.filter(m =>
    !m.student_progress?.status ||
    m.student_progress?.status === 'not_started' ||
    (m.student_progress?.status === 'in_progress' && (m.student_progress?.completion_percentage || 0) < CONTINUE_THRESHOLD)
  );
  const completedModules = sortedModules.filter(m => m.student_progress?.status === 'completed');

  // Get unique topics from modules for filter, sorted by ESLCE frequency (highest first)
  const moduleTopics = Array.from(new Set(modules.map(m => m.topic)))
    .sort((a, b) => (eslceTopicPcts[b] || 0) - (eslceTopicPcts[a] || 0));

  // Design theme - switch between 'option-a', 'option-b', or 'option-c' to preview
  const designTheme = 'option-a';

  // Overall progress percentage
  const overallProgressPct = sortedModules.length > 0
    ? Math.round((completedModules.length / sortedModules.length) * 100)
    : 0;

  // Helper to render a module card
  const renderModuleCard = (module: Module) => {
    const subjectColor = subjectColors[module.subject] || '#667eea';
    const progressStatus = module.student_progress?.status || 'not_started';
    const completionPct = module.student_progress?.completion_percentage || 0;
    const correctAnswers = module.student_progress?.correct_answers || 0;
    const totalQuestions = module.student_progress?.total_questions_answered || 0;
    const quizAccuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    const isStrongResult = totalQuestions > 0 && quizAccuracy >= 70;

    return (
      <div
        key={module.module_id}
        className={`module-card ${progressStatus}`}
        style={{ '--module-color': subjectColor } as React.CSSProperties}
        data-topic={module.topic}
        onClick={() => handleModuleSelect(module)}
      >
        {/* Desktop: show topic/subtopic badge + sibling count + diagnostic label */}
        <div className="module-card-header">
          <span className="module-topic-badge">
            {(module.subtopic && topicModuleCounts[module.topic] > 1)
              ? module.subtopic
              : module.topic}
          </span>
          {topicModuleCounts[module.topic] > 1 && (
            <span className="module-sibling-count">
              {moduleTopicIndex[module.module_id]} of {topicModuleCounts[module.topic]}
            </span>
          )}
          {module.diagnostic_label === 'focus_area' && (
            <span className="diagnostic-badge focus">Focus Area</span>
          )}
          {module.diagnostic_label === 'start_learning' && (
            <span className="diagnostic-badge new-topic">New Topic</span>
          )}
          {module.diagnostic_label === 'solid' && (
            <span className="diagnostic-badge strong">Strong</span>
          )}
        </div>

        {/* Title row with progress */}
        <div className="module-title-row">
          <h3 className="module-title">{module.title}</h3>
          <span className={`module-pct ${progressStatus}${isStrongResult ? ' strong-result' : ''}`}>
            {progressStatus === 'completed'
              ? (isStrongResult ? `${quizAccuracy}%` : '✓')
              : `${Math.round(completionPct)}%`}
          </span>
        </div>

        {module.description && (
          <p className="module-description">{module.description}</p>
        )}

        {/* Meta + action on same row */}
        <div className="module-bottom-row">
          <div className="module-meta">
            {module.total_components && (
              <span className="meta-item">
                📚 {module.total_components} items
              </span>
            )}
            {module.estimated_duration_minutes && (
              <span className="meta-item">
                ⏱️ {module.estimated_duration_minutes} min
              </span>
            )}
            {eslceTopicPcts[module.topic] > 0 && (
              <span className="meta-item eslce-yield">
                🎯 {eslceTopicPcts[module.topic]}% of ESLCE{topicModuleCounts[module.topic] > 1 ? ' (topic)' : ''}
              </span>
            )}
          </div>
          <span
            className={`module-action ${progressStatus}`}
            onClick={(e) => {
              e.stopPropagation();
              handleModuleSelect(module);
            }}
          >
            {progressStatus === 'completed'
              ? (isStrongResult ? 'Review' : 'Revisit')
              : progressStatus === 'in_progress' ? 'Continue'
              : 'Start'} →
          </span>
        </div>

        {/* Progress bar for in-progress modules */}
        {progressStatus === 'in_progress' && (
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${Math.min(100, completionPct)}%` }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`micro-learning-page ${designTheme} ${activeModule ? 'in-session' : ''}`}>
      {/* Clean minimal header */}
      <header className="page-header minimal">
        <div className="header-content">
          <button
            onClick={() => activeModule ? handleBackToModules() : navigate('/dashboard')}
            className="back-button"
          >
            {activeModule ? '✕' : '←'}
          </button>

          <div className="brand-logo" onClick={() => navigate('/dashboard')}>
            <Logo height={30} />
          </div>

          {/* Spacer for flex alignment */}
          <div className="header-spacer"></div>
        </div>
      </header>

      <main className="page-main">
        {/* Show module session if active */}
        {activeModule ? (
          <div className="module-session">
            <MicroLearningModule
              studentId={student?.student_id || 'guest'}
              subject={activeModule.subject}
              topic={activeModule.topic}
              subtopic={activeModule.subtopic}
              moduleId={activeModule.module_id}
              reviewMode={isReviewMode}
              onModuleComplete={handleModuleComplete}
              onExit={handleBackToModules}
            />
          </div>
        ) : (
          <>
            {/* Earned-moment feedback after finishing a module (Signal Engine) */}
            <FeedbackPrompt surface="web_lesson_done" trigger="module_complete" enabled={justCompletedModule} />

            {/* Subject context + Segmented mode control */}
            <div className="study-nav">
              <select
                value={selectedSubject}
                onChange={(e) => handleSubjectSelect(e.target.value)}
                className="subject-context-select"
              >
                <option value="">Choose Subject</option>
                {availableSubjects.map((subject) => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
              <div className="mode-segment">
                <button
                  className={`segment-btn ${activeTab === 'module' ? 'active' : ''}`}
                  onClick={() => setActiveTab('module')}
                >
                  📚 Lessons
                </button>
                <button
                  className={`segment-btn ${activeTab === 'practice' ? 'active' : ''}`}
                  onClick={() => setActiveTab('practice')}
                >
                  📝 Past Exams
                </button>
              </div>
            </div>

            {/* 45-Day Aptitude Path — surfaced only when Aptitude is the
                selected subject. Path is Aptitude-only by design (no
                Numerical/Vocab equivalent for other subjects yet). */}
            {selectedSubject === 'Aptitude' && (
              <button
                onClick={() => navigate('/aptitude-path')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  margin: '0 0 12px',
                  background: '#ecfdf5',
                  border: '1.5px solid #86efac',
                  borderRadius: 12,
                  width: '100%',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#0f172a',
                }}
              >
                <span style={{ fontSize: 22 }}>🗓</span>
                <span style={{ flex: 1 }}>
                  <strong>45-Day Aptitude Path</strong>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
                    One topic per day · 5 Qs · strategy + ESLCE-frequency order
                  </div>
                </span>
                <span style={{ fontSize: 20, color: '#64748b' }}>›</span>
              </button>
            )}


            {/* Topic filter chips — only when there are topics in lessons mode */}
            {selectedSubject && moduleTopics.length > 0 && activeTab === 'module' && (
              <div className="topic-chips">
                <button
                  className={`topic-chip ${!selectedTopic ? 'active' : ''}`}
                  onClick={() => handleTopicSelect('')}
                >
                  All
                </button>
                {moduleTopics.map((topic) => (
                  <button
                    key={topic}
                    className={`topic-chip ${selectedTopic === topic ? 'active' : ''}`}
                    onClick={() => handleTopicSelect(topic)}
                  >
                    {displayTopic(topic)}
                    {topicModuleCounts[topic] > 1 && (
                      <span className="chip-count">{topicModuleCounts[topic]}</span>
                    )}
                    {eslceTopicPcts[topic] > 0 && (
                      <span className="chip-count">{eslceTopicPcts[topic]}%</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Active filter indicator */}
            {selectedTopic && activeTab === 'module' && (
              <div className="active-filter-bar">
                <span className="filter-label">Showing: {displayTopic(selectedTopic)}</span>
                <button className="filter-clear" onClick={() => handleTopicSelect('')}>✕ Clear</button>
              </div>
            )}

            {/* Main Content */}
            <div className="modules-section">
              {!selectedSubject ? (
                <div className="welcome-message">
                  <div className="welcome-icon">🚀</div>
                  <h3>Ready to Crush It?</h3>
                  <p>Pick a subject above and let's get you exam-ready with focused, bite-sized lessons.</p>
                  <div className="features-preview">
                    <div className="feature-item">⏱️ Quick 5-15 min sessions</div>
                    <div className="feature-item">🎯 Exam-focused content</div>
                    <div className="feature-item">📱 Mobile-friendly</div>
                    <div className="feature-item">🏆 Track your progress</div>
                  </div>
                </div>
              ) : activeTab === 'module' ? (
                <div className="modules-content">
                  {/* CoachCard hidden for launch — resume UX handled by "Continue Where You Left Off" section below. */}
                  {/* {selectedSubject && (
                    <CoachCard
                      subject={selectedSubject}
                      location="micro_learning"
                      progressPct={overallProgressPct}
                      compact
                      onAction={(action: CoachAction) => {
                        switch (action.type) {
                          case 'continue_module': {
                            if (!action.payload) break;
                            const mod = modules.find(m => m.module_id === action.payload);
                            if (mod) handleModuleSelect(mod);
                            break;
                          }
                          case 'practice_topic':
                            setActiveTab('practice');
                            if (action.payload) setPendingDefaultTopic(action.payload);
                            break;
                          case 'start_past_exams':
                            setActiveTab('practice');
                            break;
                        }
                      }}
                    />
                  )} */}
                  {/* Module Cards Grid */}
                  {loadingModules ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading modules...</p>
                    </div>
                  ) : sortedModules.length > 0 ? (
                    <>
                      {/* Continue Learning Section - Top Priority (always show all) */}
                      {inProgressModules.length > 0 && (
                        <div className="modules-section-group priority">
                          <h3 className="section-header">
                            <span className="section-icon">▶️</span>
                            Continue Where You Left Off
                          </h3>
                          <div className="modules-grid">
                            {inProgressModules.map((module) => renderModuleCard(module))}
                          </div>
                        </div>
                      )}

                      {/* Your Achievements - Show completions! */}
                      {completedModules.length > 0 && (
                        <div className="modules-section-group achievements">
                          <h3 className="section-header">
                            <span className="section-icon">🏆</span>
                            Your Achievements
                            <span className="section-count">{completedModules.length} covered</span>
                          </h3>
                          <div className="modules-grid">
                            {(expandedSections['completed'] ? completedModules : completedModules.slice(0, SHOW_COUNT_COMPLETED))
                              .map((module) => renderModuleCard(module))}
                          </div>
                          {completedModules.length > SHOW_COUNT_COMPLETED && (
                            <button
                              className="show-more-btn"
                              onClick={() => setExpandedSections(prev => ({
                                ...prev,
                                completed: !prev.completed
                              }))}
                            >
                              {expandedSections['completed']
                                ? `Show Less ↑`
                                : `+${completedModules.length - SHOW_COUNT_COMPLETED} more modules ↓`}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Up Next Section */}
                      {notStartedModules.length > 0 && (
                        <div className="modules-section-group">
                          <h3 className="section-header">
                            <span className="section-icon">📚</span>
                            {completedModules.length > 0 || inProgressModules.length > 0 ? 'Up Next' : 'Start Learning'}
                            <span className="section-count">{notStartedModules.length} to go</span>
                          </h3>
                          <div className="modules-grid">
                            {(expandedSections['notStarted'] ? notStartedModules : notStartedModules.slice(0, SHOW_COUNT_UP_NEXT))
                              .map((module) => renderModuleCard(module))}
                          </div>
                          {notStartedModules.length > SHOW_COUNT_UP_NEXT && (
                            <button
                              className="show-more-btn"
                              onClick={() => setExpandedSections(prev => ({
                                ...prev,
                                notStarted: !prev.notStarted
                              }))}
                            >
                              {expandedSections['notStarted']
                                ? `Show Less ↑`
                                : `+${notStartedModules.length - SHOW_COUNT_UP_NEXT} more modules ↓`}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📭</div>
                      <h3>No Modules Available</h3>
                      <p>
                        {searchQuery || selectedTopic
                          ? 'No modules match your filters. Try adjusting your search.'
                          : `No published modules for ${selectedSubject} yet. Check back soon!`}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <PastESSLCEQuiz
                  subject={selectedSubject}
                  topic={pendingDefaultTopic || undefined}
                  studentId={student?.student_id}
                />
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default MicroLearning;
